// TestDetails
// -----------
// Start/stop button, scheduled/last test info, and vulnerability summary in one card.
//
// Retest was removed from the v1 UI (see the v1 vuln-state-model brief).
// The Test/Retest split pill is now a single Test button; the retest-only
// modal, the retest-only rotating message set, the retest-expiry copy,
// and the isRetest derivation are all gone from this surface.

import React, { useState, useEffect } from "react";
import { AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import TestSelector from "@/components/webApplication/TestSelector";
import {
  IN_FLIGHT_TEST_STATUSES,
  TERMINAL_TEST_STATUSES,
} from "@/constants/definitions";
import { getTestResultLabel } from "@/utils/testStatusLabel";
import TestResultPill from "@/components/common/TestResultPill";
import {
  getSeverityTileStyle,
  getSeverityLabel,
} from "@/utils/severityStyles";
import { formatLongDate, formatTime } from "@/utils/date";
import {
  EMPTY_STRING,
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
} from "@/constants/definitions";
import WebApplicationClass from "@/models/webApplication";
import TestResultsClass from "@/models/testResult";
import { eventBus, LIVE_FINDINGS_UPDATE } from "@/services/eventBus.service";

const NO_TEST_PERFORMED_MESSAGE = "No previous tests";

interface TestDetailsProps {
  webapp: WebApplicationClass;
  // testSelected drives the test-state UI: Start/Stop button,
  // Provisioning / Stopping spinners, Failed / Invalid banners,
  // rotating message, and the Report button. Always the latest test
  // attempt (any status).
  testSelected?: TestResultsClass;
  // displayTest is the terminal test the user is inspecting via the
  // TestSelector dropdown. Drives the Findings tiles here and the
  // vuln table below. Defaults to the latest terminal on the page,
  // but the user can switch via the dropdown. Undefined when no
  // terminal test exists yet.
  displayTest?: TestResultsClass;
  testRunning: boolean;
  // Filter wiring — set by clicking a severity tile, also reflected
  // by the filter pills above the vuln table. null = no filter.
  severityFilter?: string | null;
  onSeverityClick?: (severity: string) => void;
  // Test selector wiring. selectedTestId is the id of the test
  // currently displayed; onTestSelectionChange is fired when the user
  // picks a different historical test from the dropdown.
  selectedTestId?: string;
  onTestSelectionChange?: (testId: string) => void;
  // Report button lives beneath the TestSelector so the report
  // affordance is visually paired with whichever test the user has
  // picked. onOpenReport fires the Report modal; the button disables
  // when no terminal test is selected.
  onOpenReport?: () => void;
}

// Rotating Provisioning / Running / Stopping status messages now
// live in PentestCard alongside the Pentest Now button — those two
// belong together as "the action + the status of the action". This
// card is the data view (findings, badges, Last Test).

export default function TestDetails({
  webapp,
  testSelected,
  displayTest,
  testRunning,
  severityFilter,
  onSeverityClick,
  selectedTestId,
  onTestSelectionChange,
  onOpenReport,
}: TestDetailsProps) {
  // Re-render when a slim live finding lands mid-run — the model mutates
  // testSelected.liveFindings in place, so we bump local state to reflect it.
  // Only affects the "Findings so far" section; the authoritative tiles and
  // dashboard cards are untouched.
  const [, bumpLiveRender] = useState(0);
  useEffect(() => {
    const handleLiveFinding = () => bumpLiveRender((count) => count + 1);
    eventBus.on(LIVE_FINDINGS_UPDATE, handleLiveFinding);
    return () => {
      eventBus.off(LIVE_FINDINGS_UPDATE, handleLiveFinding);
    };
  }, []);

  // --- Last test ---
  // Sourced from displayTest (the latest *terminal* test), not from
  // webapp.tests[0]. That way during an in-flight run on an app with
  // prior history, "Last Test" continues to surface the most recent
  // completed assessment date — useful context — instead of being
  // hidden or pointing at the run-in-progress's startTime. When no
  // terminal exists at all, the panel renders nothing (gated in JSX).
  let lastTestDateString = NO_TEST_PERFORMED_MESSAGE;
  let lastTestTimeString = EMPTY_STRING;

  if (displayTest?.startTime) {
    lastTestDateString = formatLongDate(Number(displayTest.startTime));
    lastTestTimeString = formatTime(Number(displayTest.startTime));
  }

  // --- Test phase for the SELECTED test ---
  // The result badges (Full Test / Test Failed / Invalid Test) and
  // the banners + CAPTCHA notice all describe whichever test the
  // user has picked in the TestSelector — not the latest test
  // attempt. Picking an older Completed test while a new test is
  // mid-run should swap the badge to "Full Test" and hide the
  // Invalid banner, even though testSelected (latest attempt) is
  // still in flight or terminal-failed.
  //
  // The Testing Live badge (rendered separately below) is the only
  // status indicator that stays bound to testSelected — it's
  // describing the action, not the data being viewed.
  const selectedStatus = displayTest?.testStatus;
  // The user-visible result badge derives from testStatus + endTrigger
  // via the shared helper, so this surface agrees with the Dashboard /
  // Applications pills and the TestSelector dropdown labels. Same
  // logic the BE uses to compute setStatus.currentPhase: testStatus
  // Failed/Invalid OR endTrigger in {Cancelled, InfrastructureFailure,
  // ServerUnreachable} → "Test Failed". testStatus Completed AND a
  // clean endTrigger → "Completed".
  const resultLabel = getTestResultLabel({
    testStatus: selectedStatus,
    endTrigger: displayTest?.endTrigger,
  });
  // True only when the selected test has reached a final state —
  // drives the result-pill render and the Report button. Sourced
  // from testStatus directly (rather than resultLabel.tone) so the
  // neutral-tone Stopped Early case still counts as terminal; the
  // pill should appear and the report should be reachable for a
  // user-stopped test, even though its tone isn't success/danger.
  const isSelectedTerminal =
    !!selectedStatus && TERMINAL_TEST_STATUSES.has(selectedStatus);
  // The Invalid / Failed banners below distinguish *why* a test failed,
  // not whether it failed — kept distinct because the default copy
  // differs (server unreachable vs infra failed). Used only for banner
  // selection; the badge above uses the unified Test Failed label.
  const isFailed = selectedStatus === "Failed";
  const isInvalid = selectedStatus === "Invalid";

  // --- CAPTCHA warning ---
  // From the selected test's stats, not the latest attempt — switching
  // to an older Completed test should show that test's CAPTCHA stats,
  // not the in-flight run's.
  const captchaPages = displayTest?.statistics?.captchaPages ?? 0;

  // --- Vuln counts ---
  //
  // Derived from displayTest.vulns once they're hydrated. displayTest
  // reflects the customer's current state: the latest Completed test
  // when the latest attempt is terminal-but-not-Completed, otherwise
  // the latest test. This keeps the tiles and the vuln table below
  // speaking from one source — including the case where the latest
  // attempt failed, in which case both surfaces revert to the previous
  // Completed test rather than showing a partial / empty view.
  //
  // Important: do NOT fall back to testSelected when displayTest is
  // undefined. That happens when the only test the user has ever run
  // failed (no prior Completed exists). The vuln table receives
  // displayTest directly and renders empty in this case, so a fallback
  // here would surface the failed test's partial observations on the
  // tiles while the table stayed empty — a confusing divergence that
  // was the root cause of the "critical count says 1 but the table is
  // empty" bug after a ServerUnreachable.
  //
  // Before hydration completes we fall back to the stored aggregate
  // (webAppTestStatus.opencrits etc.) so the tiles don't flash 0 during
  // the initial load. This fallback only applies when displayTest exists
  // but its vulns haven't been hydrated yet — never as a substitute for
  // a missing displayTest. Per the v1 brief §0.5, the BE is responsible
  // for keeping that aggregate tied to currentTest as well.
  //
  // Not memoized: vulns mutate in place (status flips don't change the
  // array reference), so any memo would silently return cached values.
  // The walk is O(vulns) — cheap, and runs only on parent re-render.
  const vulnsSource = displayTest;
  const hasDisplayTest = !!vulnsSource;
  // True when the user has selected an in-flight test (Provisioning,
  // Running, or Stopping) — the tiles then report findings-so-far rather
  // than falling back to the stored aggregate for an unhydrated test.
  const isSelectedInFlight =
    !!vulnsSource?.testStatus &&
    IN_FLIGHT_TEST_STATUSES.has(vulnsSource.testStatus);
  // getDisplayVulns() rather than .vulns: for an in-flight run it unions the
  // backend-persisted findings with the rows the discovery subscription has
  // streamed since the last fetch. Identical to .vulns once terminal.
  //
  // MUST be sourced from displayTest — the same object VulnerabilityTable
  // renders. A previous version tallied the in-flight numbers from the
  // `testSelected` prop instead, which is React state holding an object
  // REFERENCE, while displayTest is looked up out of webapp.tests on every
  // render. When those resolved to two different instances of the same test
  // (as happens after a mid-run page refresh, where the backfilled instance
  // is hydrated but the state snapshot is not), the tiles counted one object
  // and the table counted another: 8 findings listed below tiles reading
  // 0/0/0/1/0. One object, one walk, and they cannot disagree.
  const vulnsList = vulnsSource?.getDisplayVulns() ?? [];
  // No vulnStatus filter: in v1 every vuln on the latest test is current
  // state by definition (status is no longer user-mutable and the BE
  // emits Open for new findings). Older records may carry an empty or
  // legacy vulnStatus value, so filtering by `vulnStatus === Open`
  // silently dropped legitimate findings — that's why the tiles could
  // read lower than the vuln-table count below.
  let derivedCrits = 0, derivedHighs = 0, derivedMeds = 0, derivedLows = 0, derivedInfo = 0;
  for (const vulnEntry of vulnsList) {
    if (vulnEntry.riskRating === RISK_RATING_INFO) {
      derivedInfo++;
      continue;
    }
    if (vulnEntry.riskRating === RISK_RATING_CRITICAL) derivedCrits++;
    else if (vulnEntry.riskRating === RISK_RATING_HIGH) derivedHighs++;
    else if (vulnEntry.riskRating === RISK_RATING_MED)  derivedMeds++;
    else if (vulnEntry.riskRating === RISK_RATING_LOW)  derivedLows++;
  }
  const hasVulnsLoaded = !!vulnsSource?.fullResultLoaded;

  // The derived counts above are the live counts during a run: vulnsList is
  // already the persisted-plus-streamed union, and it grows as findings
  // arrive (the model appends to liveFindings in place and emits
  // LIVE_FINDINGS_UPDATE, which bumps this component's render via the effect
  // at the top of the file). There is deliberately no second tally.
  // Does the selected test have any findings to speak for itself with? A
  // test can hold findings without being hydrated: the live discovery
  // stream's rows survive on the model after the run ends, and they are the
  // only record this session has if the post-terminal fetch failed. In that
  // state the stored aggregate is the wrong answer — it describes whatever
  // the backend last summarised, not the test in front of the user.
  const hasFindingsToShow = vulnsList.length > 0;
  const resolveCount = (derived: number, aggregate: number): number => {
    // An in-flight test reports what has been found so far. The stored
    // aggregate is NOT a valid fallback here — it describes the last
    // completed test, so using it mid-run would show the previous test's
    // numbers above the current run's findings.
    if (isSelectedInFlight) return derived;
    if (!hasDisplayTest) return 0;
    // The aggregate is a loading-flicker guard and nothing more: it applies
    // only to a test that is both unhydrated AND holding no findings of its
    // own. Anything we can actually count beats a summary of something else.
    if (hasVulnsLoaded || hasFindingsToShow) return derived;
    return aggregate;
  };
  const opencrits = resolveCount(derivedCrits, webapp.webAppTestStatus.opencrits);
  const openhighs = resolveCount(derivedHighs, webapp.webAppTestStatus.openhighs);
  const openmeds  = resolveCount(derivedMeds,  webapp.webAppTestStatus.openmeds);
  const openlows  = resolveCount(derivedLows,  webapp.webAppTestStatus.openlows);
  // Info has no stored aggregate to fall back to, so it reads the derived
  // count in every state.
  const infoCount = derivedInfo;

  // True when a test is in flight and the tiles + table are showing
  // the previous terminal test's findings rather than the in-flight
  // run's data. Drives the "showing previous test" caption next to
  // the Findings heading so the user understands the surface is
  // intentionally not live during the run.
  //
  // Compared by id, not object identity: displayTest is looked up from
  // webapp.tests on each render while testSelected is a state-held
  // reference, so the same test can be two instances. Identity comparison
  // then claimed the user was viewing a previous test while they were in
  // fact looking at the live one.
  const isShowingPreviousResults =
    !!displayTest && !!testSelected && displayTest.id !== testSelected.id;

  return (
    // No Card wrapper — this surface now lives inside a unified Card
    // in WebApplication.tsx alongside PentestCard. The outer div fills
    // the right 3/4 column; the inner structure (header bar with
    // border-b, body row, banners) remains visually identical to the
    // pre-merge layout.
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">Security Status</h2>
            {/* Testing Live — sibling to the test-result badges below.
                Fires whenever the webapp's testRunning is true (covers
                Provisioning, Running, Stopping). Pairs with the pulsing
                dot so the state reads at-a-glance from the header bar
                without the user having to track the Pentest button
                across the row. */}
            {testRunning && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                  <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500" />
                </span>
                Testing Live
              </span>
            )}
            {/* Result badge — single source for "what was the outcome
                of the selected test". Suppressed while a new test is
                running; the Testing Live badge above takes over.
                Wording / color come from the shared helper so this
                surface agrees with every other status pill in the
                app. */}
            {!testRunning && isSelectedTerminal && (
              <TestResultPill resultLabel={resultLabel} size="md" />
            )}
          </div>
          {/* Test selector + Report side-by-side. The two sit on the
              same line so the report affordance is visually paired
              with whichever test is picked in the dropdown — clicking
              Report generates *that* test's report. The Report button
              uses the shadcn outline-sm style (larger than the
              dropdown trigger) to read clearly as a primary action
              within this card. TestSelector hides itself when no
              terminal tests exist; Report hides too in that case
              (nothing to report on). */}
          {onTestSelectionChange && (
            <div className="flex items-center gap-2">
              <TestSelector
                tests={webapp.tests}
                selectedTestId={selectedTestId ?? ""}
                onChange={onTestSelectionChange}
              />
              {/* Disabled (not hidden) when the selected test is in-
                  flight or there's no terminal data to report on
                  yet. Disabling rather than hiding keeps the button
                  in the layout — the user always sees Report exists,
                  there's no "where did it go?" moment when the
                  selection or test state changes. */}
              {onOpenReport && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenReport}
                  disabled={!isSelectedTerminal}
                  className="gap-1.5"
                  title={
                    isSelectedTerminal
                      ? "Generate a report for the selected test"
                      : "Report available once the test reaches a terminal status"
                  }
                >
                  <FileText className="h-3.5 w-3.5" />
                  Report
                </Button>
              )}
            </div>
          )}
        </div>

      <div className="flex-1 flex flex-col">
        <div className="flex items-center px-10 py-8">

            {/* Findings counts. The five severity tiles are the single
                findings surface in both states:
                  - Terminal test selected → the test's final counts.
                  - In-flight run selected → live counts that climb as
                    the discovery subscription streams findings in (see
                    the live tally above). A "test in progress" pulse
                    labels them as still-growing.
                The four risk tiles act as filter shortcuts when
                onSeverityClick is provided (re-click toggles back to
                All); the Info tile stays decorative. Filtering works in
                both states — the vuln table below renders live rows
                during a run, so filtering a severity narrows those too.
                When a test is in flight but the user has switched to a
                previous terminal test, a "showing previous test" caption
                signals the surface is intentionally not live. */}
            <div className="shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Findings</p>
                {isSelectedInFlight ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600">
                    <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
                      <span className="animate-ping motion-reduce:animate-none absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
                    </span>
                    test in progress
                  </span>
                ) : isShowingPreviousResults ? (
                  <p className="text-[10px] text-gray-400 italic">
                    showing previous test
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                {[
                  { severity: RISK_RATING_CRITICAL, count: opencrits },
                  { severity: RISK_RATING_HIGH,     count: openhighs },
                  { severity: RISK_RATING_MED,      count: openmeds  },
                  { severity: RISK_RATING_LOW,      count: openlows  },
                  { severity: RISK_RATING_INFO,     count: infoCount },
                ].map(({ severity, count }) => {
                  const tile = getSeverityTileStyle(severity);
                  const isActive = severityFilter === severity;
                  return (
                    <button
                      key={severity}
                      type="button"
                      onClick={() => onSeverityClick?.(severity)}
                      disabled={!onSeverityClick}
                      className={`flex flex-col items-center rounded-xl ${tile.bg} py-3 w-24 transition-all ${
                        onSeverityClick
                          ? `cursor-pointer ${tile.hoverBg} hover:shadow-sm`
                          : "cursor-default"
                      } ${isActive ? `ring-2 ${tile.ring} ring-offset-2` : ""}`}
                    >
                      <p className={`text-3xl font-bold ${tile.numberText} tabular-nums leading-none`}>{count}</p>
                      <p className={`text-xs font-medium ${tile.labelText} mt-1.5`}>{getSeverityLabel(severity)}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Vertical divider */}
            <div className="w-px bg-gray-100 self-stretch mx-8" />

            {/* Last Test metadata. Sourced from displayTest (latest
                terminal), so during an in-flight run this still shows
                the most recent completed assessment date — useful
                context that the prior implementation hid. Rendered
                only when a terminal test actually exists; brand-new
                apps with no completed runs yet skip the panel. */}
            <div className="flex-1 min-w-0">
              {!!displayTest && (
                <>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Last Test
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900 truncate">{lastTestDateString}</p>
                  {lastTestTimeString && (
                    <p className="text-xs text-gray-400">{lastTestTimeString}</p>
                  )}
                </>
              )}
            </div>

          </div>

          {/* Invalid test warning — sourced from the SELECTED test's
              testStatusMessage so switching to an older Completed
              test hides this banner. */}
          {isInvalid && (
            <div className="mx-10 mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/70 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                {displayTest?.testStatusMessage || "The server was unreachable during this test. Results are invalid. Please ensure the server is running and accessible, then start a new test."}
              </p>
            </div>
          )}

          {/* Failed test warning — same selection-driven source. */}
          {isFailed && (
            <div className="mx-10 mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/70 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">
                {displayTest?.testStatusMessage || "The test environment failed to start. Please try again."}
              </p>
            </div>
          )}

          {/* CAPTCHA warning — describes the selected test's CAPTCHA
              stats, independent of any in-flight latest. Only fires
              when the selected test reached "Completed" cleanly; for
              failed runs the failure banner takes precedence. */}
          {captchaPages > 0 && resultLabel.tone === "success" && (
            <div className="mx-10 mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                <strong>{captchaPages}</strong> page{captchaPages > 1 ? "s" : ""} contained CAPTCHA challenges — forms on {captchaPages > 1 ? "these pages were" : "this page were"} not tested.
                Consider disabling CAPTCHAs on your test environment for full coverage.
              </p>
            </div>
          )}

      </div>
    </div>
  );
}
